import { logger } from '@4lch4/logger'
import Dayjs from 'dayjs'
import fs from 'fs-extra'
import { join } from 'path'
import { IAppConfig } from '../../interfaces/index.js'
import {
  DEFAULT_DATA_DIR,
  DEFAULT_FILENAME_DATE_FORMAT,
  DEFAULT_FS_WRITE_OPTIONS
} from '../index.js'

/**
 * A utility class for interacting with the filesystem. Provides helper methods
 * for storing responses as a JSON file.
 */
export class IOUtil {
  constructor(private config: Required<IAppConfig>) {}

  public getFilePath(filename: string, dateFormat: string = DEFAULT_FILENAME_DATE_FORMAT) {
    // Create a date string using the given format.
    const dateString = Dayjs().format(dateFormat)

    // Set filename to `filename-YYYY.MM.DD-HH.mm.ss.json`
    const newFilename = `${filename}-${dateString}.json`

    // If the `debug` flag is enabled, output the variables thusfar.
    if (this.config.debug) {
      logger.info(`[IOUtil#getFilePath]: filename = ${filename}`)
      logger.info(`[IOUtil#getFilePath]: dateFormat = ${dateFormat}`)
      logger.info(`[IOUtil#getFilePath]: dateString = ${dateString}`)
      logger.info(`[IOUtil#getFilePath]: newFilename = ${newFilename}`)
    }

    return join(this.config.dataDir || DEFAULT_DATA_DIR, newFilename)
  }

  /**
   * Writes the given data to the filesystem as a JSON file with a name using
   * the following format: `<Filename>-YYYY.MM.DD-HH.mm.ss.json`
   *
   * It results in a filename like `bills-2022.01.01-12.00.00.json`.
   *
   * @param filename The name of the file to write to.
   * @param data The data to save to the file.
   * @param writeOptions The options to use when writing to the file.
   *
   * @returns A promise that resolves when the file has been saved.
   */
  public async saveFile(
    filename: string,
    data: any,
    writeOptions: fs.WriteOptions = DEFAULT_FS_WRITE_OPTIONS
  ): Promise<void> {
    const filePath = this.getFilePath(filename)

    await fs.ensureFile(filePath)

    logger.info(`[IOUtil#saveFile]: Saving file to ${filePath}`)

    return fs.writeJSON(filePath, data, writeOptions)
  }
}
